import { createRoute, Link, useNavigate, useSearch } from "@tanstack/solid-router";
import { createSignal, Show } from "solid-js";
import { authRoute } from "./route";
import { auth } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@fleet/ui";
import { AnimatedSection } from "@/components/ui/animated-section";
import { FolioScene } from "@/components/auth/folio-scene";

function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_auth/reset-password" });
  const { t } = useI18n();
  const [password, setPassword] = createSignal("");
  const [confirm, setConfirm] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (password() !== confirm()) {
      setError(t(msg`Passwords do not match`));
      return;
    }
    const { token } = search();
    if (!token) {
      setError(t(msg`Invalid or expired recovery link`));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await auth.resetPassword({
        new_password: password(),
        token,
      });
      await navigate({ to: "/login" });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t(msg`Unexpected error. Please try again.`));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FolioScene
      eyebrow={t(msg`Recovery`)}
      headline={t(msg`Choose a new password.`)}
      tagline={t(msg`Set a new password for your account and you're back in.`)}
      footer={
        <p class="text-xs text-muted-foreground/60">
          <Link to="/" class="transition-colors hover:text-primary">
            {t(msg`← Back to home`)}
          </Link>
        </p>
      }
    >
      <Show
        when={search().token}
        fallback={
          <div class="space-y-4">
            <div class="space-y-1 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-center text-sm">
              <p class="font-medium text-destructive">{t(msg`Invalid link`)}</p>
              <p class="text-muted-foreground">
                {t(msg`This recovery link is invalid or has expired. Please request a new one.`)}
              </p>
            </div>
            <Link to="/forgot-password" class="block">
              <Button variant="outline" class="w-full">
                {t(msg`Request new link`)}
              </Button>
            </Link>
          </div>
        }
      >
        <form onSubmit={(e) => void handleSubmit(e)} class="space-y-4">
          <div class="space-y-2">
            <Label for="password">{t(msg`New Password`)}</Label>
            <TextField
              id="password"
              type="password"
              placeholder={t(msg`••••••••`)}
              autocomplete="new-password"
              required
              minLength={8}
              disabled={loading()}
              value={password()}
              onInput={(e) => setPassword(e.currentTarget.value)}
            />
          </div>
          <div class="space-y-2">
            <Label for="confirm">{t(msg`Confirm Password`)}</Label>
            <TextField
              id="confirm"
              type="password"
              placeholder={t(msg`••••••••`)}
              autocomplete="new-password"
              required
              minLength={8}
              disabled={loading()}
              value={confirm()}
              onInput={(e) => setConfirm(e.currentTarget.value)}
            />
          </div>
          <AnimatedSection show={Boolean(error())}>
            <p class="text-sm text-destructive">{error()}</p>
          </AnimatedSection>

          <div class="flex flex-col gap-3 pt-1">
            <Button type="submit" class="w-full" loading={loading()}>
              {loading() ? t(msg`Saving…`) : t(msg`Save Password`)}
            </Button>
            <p class="text-center text-sm text-muted-foreground">
              <Link to="/login" class="font-medium text-primary underline-offset-4 hover:underline">
                {t(msg`Back to sign in`)}
              </Link>
            </p>
          </div>
        </form>
      </Show>
    </FolioScene>
  );
}

export const resetPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: ResetPasswordPage,
});
